/**
 * What the AI assistant can add to a search result, beyond the plain list.
 * Shown as a card above the normal results (see AssistantCard.tsx).
 *
 *  - "clarify": the request was ambiguous between different tools, so it
 *    asks one short question and offers tappable answers, each leading
 *    straight to a real tool (or a guided pipeline for several).
 *  - "answer": the visitor asked a question about the tools or the site;
 *    a short written answer plus links to related tools.
 *  - "workflow": the request needs several tools in order. A short
 *    explanation (naming any assumption it made, e.g. an unspecified file
 *    type) plus the ordered step list and a link to run them as a guided
 *    pipeline. This is the one place a multi-step suggestion should ever
 *    render -- the side panel suppresses its own plain keyword-based
 *    multi-step guess whenever this is present, so the person sees one
 *    nuanced, AI-aware answer instead of a redundant, context-blind one
 *    appearing above it.
 *
 * Every string here came from a language model, so it is only ever
 * rendered as plain text (never as HTML), and every link is built by OUR
 * code from tool IDs that were checked against the tools we actually sent
 * -- the model never supplies a URL.
 */
export interface AssistantOption {
  label: string;
  toolIds: string[];
  /** Built from toolIds by hrefForTools -- never taken from the model. */
  href: string;
}

export interface AssistantRelatedTool {
  id: string;
  name: string;
  href: string;
}

export interface AssistantWorkflowStep {
  id: string;
  name: string;
}

export type AssistantMessage =
  | { kind: "clarify"; text: string; options: AssistantOption[] }
  | { kind: "answer"; text: string; tools: AssistantRelatedTool[] }
  | { kind: "workflow"; text: string; steps: AssistantWorkflowStep[]; href: string };

export function hrefForTools(toolIds: string[]): string {
  return toolIds.length === 1
    ? `/tool/${toolIds[0]}`
    : `/pipeline?tools=${encodeURIComponent(toolIds.join(","))}`;
}
